import type { ContextMenuItem } from "@cmmd-forge/contracts";

/**
 * Imperative DOM-based context menu for non-Electron environments.
 * Shows a positioned dropdown and returns a promise that resolves
 * with the clicked item id, or null if dismissed.
 */
/** One row of the flattened menu: an action, or a group's label. */
export interface FlattenedContextMenuRow<T extends string> {
  readonly item: ContextMenuItem<T>;
  readonly isGroupLabel: boolean;
}

/**
 * Flatten one level of grouping into the flat list this menu renders.
 *
 * Electron renders `children` as a real submenu. This fallback has no
 * hover-submenu machinery — no intent delay, no nested positioning, no keyboard
 * traversal — and building that here would be a second menu implementation to
 * keep correct. A group instead renders as a non-interactive label with its
 * children beneath it: the same actions, the same ids, reachable in one click
 * instead of two.
 *
 * An EMPTY `children` array and an ABSENT one mean different things. Absent is
 * a plain action and renders normally; empty is a group whose actions all
 * dropped out, and renders nothing rather than a label with no body.
 *
 * Pure and exported so the rule is testable without driving the DOM.
 */
export function flattenContextMenuItems<T extends string>(
  items: readonly ContextMenuItem<T>[],
): ReadonlyArray<FlattenedContextMenuRow<T>> {
  const rows: FlattenedContextMenuRow<T>[] = [];
  for (const item of items) {
    const children = item.children ?? [];
    if (children.length > 0) {
      rows.push({ item, isGroupLabel: true });
      for (const child of children) rows.push({ item: child, isGroupLabel: false });
      continue;
    }
    if (Array.isArray(item.children)) continue;
    rows.push({ item, isGroupLabel: false });
  }
  return rows;
}

/** The non-interactive heading a group renders as in this fallback. */
function createGroupLabel(label: string): HTMLDivElement {
  const element = document.createElement("div");
  element.textContent = label;
  element.className =
    "px-2 pt-1.5 pb-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70";
  return element;
}

/** One action row. Styling only — the click handler is bound by the caller. */
function createMenuButton(input: {
  readonly label: string;
  readonly disabled: boolean;
  readonly destructive: boolean;
}): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = input.label;
  btn.disabled = input.disabled;
  btn.className = input.disabled
    ? "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] text-muted-foreground/50 cursor-not-allowed"
    : input.destructive
      ? "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] text-destructive transition-colors hover:bg-accent"
      : "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] text-popover-foreground transition-colors hover:bg-accent";
  return btn;
}

export function showContextMenuFallback<T extends string>(
  items: readonly ContextMenuItem<T>[],
  position?: { x: number; y: number },
): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;z-index:9999";

    const menu = document.createElement("div");
    // Match the design-system menu surface (ui/menu.tsx / ui/popover.tsx):
    // rounded-lg + shadow-lg/5 + the `before:` inner-edge highlight. The old
    // `shadow-xl` was never compiled (nothing else uses it), so the menu
    // rendered with no elevation — flat against the dark background.
    menu.className =
      "fixed z-[10000] min-w-[160px] rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg/5 outline-none before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]";

    const x = position?.x ?? 0;
    const y = position?.y ?? 0;
    menu.style.top = `${y}px`;
    menu.style.left = `${x}px`;

    function cleanup(result: T | null) {
      document.removeEventListener("keydown", onKeyDown);
      overlay.remove();
      menu.remove();
      resolve(result);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        cleanup(null);
      }
    }

    overlay.addEventListener("mousedown", () => cleanup(null));
    document.addEventListener("keydown", onKeyDown);

    let destructiveSeparatorInserted = false;
    for (const { item, isGroupLabel } of flattenContextMenuItems(items)) {
      if (isGroupLabel) {
        menu.appendChild(createGroupLabel(item.label));
        continue;
      }
      const isDestructiveAction = item.destructive === true || item.id === "delete";
      const isDisabled = item.disabled === true;

      // Group destructive actions off with a divider (Linear/Notion pattern) so
      // Delete never sits flush against the safe actions above it.
      if (isDestructiveAction && !destructiveSeparatorInserted) {
        const separator = document.createElement("div");
        separator.className = "-mx-1 my-1 h-px bg-border";
        menu.appendChild(separator);
        destructiveSeparatorInserted = true;
      }

      const btn = createMenuButton({
        label: item.label,
        disabled: isDisabled,
        destructive: isDestructiveAction,
      });
      if (!isDisabled) {
        btn.addEventListener("click", () => cleanup(item.id));
      }
      menu.appendChild(btn);
    }

    document.body.appendChild(overlay);
    document.body.appendChild(menu);

    // Adjust if menu overflows viewport
    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect();
      if (rect.right > window.innerWidth) {
        menu.style.left = `${window.innerWidth - rect.width - 4}px`;
      }
      if (rect.bottom > window.innerHeight) {
        menu.style.top = `${window.innerHeight - rect.height - 4}px`;
      }
    });
  });
}
